import React, { useEffect, useMemo } from 'react';
import { useForm, SubmitHandler, Controller, useFieldArray, useWatch } from 'react-hook-form';
import { AdminCreateInvoicePayload, AdminCreateInvoiceResponse, BasketItem } from '@dopomogai/api-client';
import { Modal, TextField, SelectField, Button, Spinner, IconButton, Icon, TextareaField, Alert } from '@dopomogai/ui';
import { useCreateInvoice } from '@dopomogai/agent-kit';

type InvoiceFormInputs = AdminCreateInvoicePayload;

const currencyOptions = [
  { value: 'UAH', label: 'Ukrainian Hryvnia (UAH)' },
  { value: 'USD', label: 'US Dollar (USD)' },
  { value: 'EUR', label: 'Euro (EUR)' },
];

const defaultItem: BasketItem = { name: '', quantity: 1, amount: 0, unit: 'pcs' };
const defaultFormValues: InvoiceFormInputs = {
  items: [defaultItem],
  destination: '',
  currency: 'UAH',
  comment: '',
  validity_days: 3,
};

interface InvoiceCreatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (response: AdminCreateInvoiceResponse) => void;
  initialData?: AdminCreateInvoicePayload | null;
}

export const InvoiceCreatorModal: React.FC<InvoiceCreatorModalProps> = ({ isOpen, onClose, onSuccess, initialData }) => {
  const { control, handleSubmit, reset, formState: { errors } } = useForm<InvoiceFormInputs>({
    defaultValues: initialData || defaultFormValues,
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'items' });
  const { createInvoice, data: invoiceData, error: apiError, isLoading } = useCreateInvoice();

  const watchedItems = useWatch({ control, name: 'items' });
  const watchedCurrency = useWatch({ control, name: 'currency' });
  
  const totalAmount = useMemo(() => {
    return (watchedItems || []).reduce((sum, item) => sum + (Number(item.amount) || 0) * (Number(item.quantity) || 0), 0);
  }, [watchedItems]);

  useEffect(() => {
    // When the modal opens, reset the form with the appropriate data.
    // If initialData is provided (for duplicating), use it.
    // Otherwise, use the blank default values.
    if (isOpen) {
      reset(initialData || defaultFormValues);
    }
  }, [isOpen, initialData, reset]);

  const onSubmit: SubmitHandler<InvoiceFormInputs> = async (formData) => {
    const processedData: AdminCreateInvoicePayload = {
      ...formData,
      items: formData.items.map(item => ({
        ...item,
        quantity: Number(item.quantity),
        amount: Number(item.amount),
      })),
    };
    await createInvoice(processedData);
  };
  
  useEffect(() => {
    if (invoiceData) {
      onSuccess(invoiceData);
    }
  }, [invoiceData, onSuccess]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create New Invoice" size="xl">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Dynamic Items Section */}
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">Invoice Items</h3>
          {fields.map((field, index) => (
            <div key={field.id} className="grid grid-cols-12 gap-x-4 gap-y-3 p-4 bg-slate-50 rounded-lg border">
              <div className="col-span-12 sm:col-span-5">
                <Controller name={`items.${index}.name`} control={control} rules={{ required: 'Item name is required' }} render={({ field }) => <TextField label="Item Name" {...field} errorText={errors.items?.[index]?.name?.message} />} />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <Controller name={`items.${index}.quantity`} control={control} rules={{ required: true, min: 1 }} render={({ field }) => <TextField label="Qty" type="number" {...field} />} />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <Controller name={`items.${index}.amount`} control={control} rules={{ required: true, min: 0.01 }} render={({ field }) => <TextField label="Price" type="number" step="0.01" {...field} />} />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <Controller name={`items.${index}.unit`} control={control} render={({ field }) => <TextField label="Unit" {...field} />} />
              </div>
              <div className="col-span-12 sm:col-span-1 flex items-end justify-end sm:justify-center">
                <IconButton icon={<Icon name="Trash2"/>} ariaLabel="Remove item" variant='ghost' onClick={() => remove(index)} disabled={fields.length <= 1} />
              </div>
            </div>
          ))}
          <Button text="Add Item" variant="outline" icon={<Icon name="Plus"/>} onClick={() => append(defaultItem)} />
        </div>

        <hr />
        
        {/* Static Fields Section */}
        {/* --- MODIFIED: Added placeholder to TextareaField --- */}
        <Controller name="destination" control={control} rules={{ required: 'Destination is required' }} render={({ field }) => <TextareaField label="Invoice Destination (Visible to Client)" {...field} errorText={errors.destination?.message} rows={3} placeholder="e.g., Payment for services. Subject to Public Offer Agreement." />} />
        
        {/* --- MODIFIED: Added validity field --- */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Controller name="currency" control={control} render={({ field }) => <SelectField label="Currency" options={currencyOptions} {...field} />} />
          </div>
          <Controller
              name="validity_days"
              control={control}
              rules={{ required: "Validity is required", min: 1, max: 30 }}
              render={({ field }) => (
                <TextField 
                  label="Validity (days)" 
                  type="number" 
                  {...field}
                  onChange={e => field.onChange(parseInt(e.target.value, 10))}
                  errorText={errors.validity_days?.message}
                />
              )}
            />
        </div>
        
        <Controller name="comment" control={control} render={({ field }) => <TextField label="Internal Comment (Optional)" {...field} />} />
        
        {apiError && <Alert type="error" title="Error Creating Invoice" message={apiError.message} />}

        {/* Footer & Totals */}
        <div className="flex flex-col sm:flex-row justify-between items-center pt-4 border-t mt-6 gap-4">
          <div className="font-bold text-xl self-start sm:self-center">
            Total: {totalAmount.toFixed(2)} {watchedCurrency}
          </div>
          <div className="flex gap-3 self-end sm:self-center">
            <Button text="Cancel" variant="outline" onClick={onClose} disabled={isLoading} />
            <Button text={isLoading ? "Creating..." : "Create Invoice"} type="submit" disabled={isLoading} icon={isLoading ? <Spinner size="sm" /> : <Icon name="FilePlus"/>} />
          </div>
        </div>
      </form>
    </Modal>
  );
};